/**
 * webmcp-setup.ts — build the Studio-level CogoToolRegistry for Phase 4.
 *
 * Seeds the registry from the static `ontology-manifest.json` under
 * `public/` and wires each tool to a real handler in `tool-handlers.ts`.
 * Follows the same pattern as `cogo-starter/src/agent/webmcp-setup.ts`.
 *
 * Studio-specific note: handlers perform shell-gating themselves
 * (ToolGateError for wrong-shell calls). They are always registered so
 * the LLM can observe the full tool surface; the gating is enforced at
 * invocation time, after the user has had a chance to approve/reject.
 */

import {
  CogoToolRegistry,
  type ToolManifestEntry,
  type WebMCPTool,
} from '@cogo/webmcp-runtime/react'

// Manifest is duplicated under `public/` (served statically for external
// consumers — e.g. Claude Code) and `src/agent/manifest.json` (bundled at
// build time). Keep them in sync; the build-time copy is what the
// WebMCPProvider initializes from. See Phase 4 plan § "ontology-manifest.json".
import ontologyManifest from './manifest.json'
import {
  STUDIO_TOOL_HANDLERS,
  ToolGateError,
  isStudioCapabilityGuardedAlias,
  planStudioCapabilityGuardedAlias,
} from './tool-handlers'
import { mcpBus, type StudioMcpBus } from '@/mcp/studio-mcp-bus'
import { findStudioChatOperationsByToolName } from '@/features/chat/services/studio-chat-operation-registry'

/**
 * Shape of the Studio ontology manifest. Matches `WebMCPOntologyManifest`
 * (from `@cogo/webmcp-runtime/react`) but narrows the optional fields we
 * actually populate. Loose index signature satisfies the runtime's
 * `[key: string]: unknown` requirement.
 */
export interface StudioOntologyManifest {
  version?: string
  persona?: string
  narrativeContext?: string
  tools: ToolManifestEntry[]
  states?: unknown[]
  actions?: unknown[]
  [key: string]: unknown
}

// Cast via `unknown` — the JSON's inferred type has undefined-slot unions
// on `inputSchema.properties` that don't index-signature-assign to
// ToolManifestEntry[]. Runtime shape is correct and smoke-tested.
export const STUDIO_ONTOLOGY = ontologyManifest as unknown as StudioOntologyManifest

/**
 * Wrap a raw handler so thrown errors (including ToolGateError) surface as
 * structured `{ ok: false, error }` payloads. The agent UI prefers a
 * thrown exception (shows as "error" in the transcript), but we leave
 * ToolGateError propagating so the UI can distinguish gate refusals from
 * genuine bugs via the error message.
 */
function wrapHandler(name: string) {
  if (isStudioCapabilityGuardedAlias(name)) {
    return async (params: Record<string, unknown>) =>
      planStudioCapabilityGuardedAlias(name, params)
  }
  const handler = STUDIO_TOOL_HANDLERS[name]
  if (handler) {
    return async (params: Record<string, unknown>) => {
      // Let ToolGateError propagate — the UI renders the message verbatim.
      // Other errors also propagate so `setEntryStatus({status:'error'})` fires.
      return await handler(params)
    }
  }
  return async () => {
    throw new ToolGateError(`No handler registered for tool "${name}".`)
  }
}

/**
 * `bus` defaults to the production singleton but is injectable so tests can
 * exercise this gate against a scoped `StudioMcpBus` (e.g. a synthetic
 * mutating tool with no chat-operation contract) without touching the
 * shared singleton. The pre-check below only throws a friendlier redirect
 * message when a KNOWN contract requires confirmation; for a tool with NO
 * contract at all it falls through to `bus.executeWebMcp`, whose own
 * registry-derived approval floor (`StudioMcpBus.requiresCapabilityApproval`)
 * is the actual, unconditional stop for that case — see that method's
 * doc comment for why this gate deliberately does not re-derive the same
 * floor a second time.
 */
function wrapStudioMcpHandler(name: string, bus: StudioMcpBus = mcpBus) {
  return async (params: Record<string, unknown>) => {
    const guardedOperations = findStudioChatOperationsByToolName(name).filter(
      (operation) => operation.requiresConfirmation,
    )
    if (guardedOperations.length > 0) {
      throw new ToolGateError(
        `Use cogo.studio.capability.executeOperation with one of these operationId values: ${guardedOperations
          .map((operation) => operation.id)
          .join(', ')}. Direct WebMCP mutation is blocked so Studio can confirm, read back, and undo the change.`,
      )
    }
    const result = await bus.executeWebMcp({ tool: name, params })
    if (!result.success) {
      throw new ToolGateError(result.error ?? `Studio MCP tool "${name}" failed.`)
    }
    return result.data ?? { ok: true, tool: name }
  }
}

function withStudioMcpBridgeMetadata(
  entry: ToolManifestEntry,
): ToolManifestEntry['metadata'] {
  return {
    ...entry.metadata,
    tags: [
      ...new Set([
        ...((entry.metadata?.tags as string[] | undefined) ?? []),
        'studio-mcp',
        'webmcp-bridge',
      ]),
    ],
  }
}

export function syncStudioMcpToolsIntoRegistry(
  registry: CogoToolRegistry,
  bus: StudioMcpBus = mcpBus,
): number {
  let added = 0
  for (const entry of bus.getManifest()) {
    const tags = (entry.metadata?.tags as string[] | undefined) ?? []
    if (tags.includes('internal')) continue
    if (registry.getTool(entry.name)) continue
    registry.register({
      name: entry.name,
      description: entry.description,
      inputSchema: entry.inputSchema,
      outputSchema: entry.outputSchema,
      metadata: withStudioMcpBridgeMetadata(entry),
      handler: wrapStudioMcpHandler(entry.name, bus),
    })
    added += 1
  }
  return added
}

export function subscribeStudioMcpToolsToRegistry(
  registry: CogoToolRegistry,
  bus: StudioMcpBus = mcpBus,
): () => void {
  let disposed = false
  let scheduled = false
  const schedule =
    typeof queueMicrotask === 'function'
      ? queueMicrotask
      : (callback: () => void) => {
          void Promise.resolve().then(callback)
        }

  const unsubscribe = bus.onToolsChanged(() => {
    if (scheduled) return
    scheduled = true
    schedule(() => {
      scheduled = false
      if (disposed) return
      syncStudioMcpToolsIntoRegistry(registry, bus)
    })
  })

  return () => {
    disposed = true
    unsubscribe()
  }
}

/** Create + populate a CogoToolRegistry from the Studio manifest. */
export function createStudioRegistry(): CogoToolRegistry {
  const registry = new CogoToolRegistry()
  for (const entry of STUDIO_ONTOLOGY.tools) {
    const tool: WebMCPTool = {
      name: entry.name,
      description: entry.description,
      inputSchema: entry.inputSchema,
      outputSchema: entry.outputSchema,
      metadata: entry.metadata,
      handler: wrapHandler(entry.name),
    }
    registry.register(tool)
  }
  syncStudioMcpToolsIntoRegistry(registry)
  return registry
}
